import { request } from '../request';
import { PaginatedResponse } from '@libs/models/common';
import { Shift } from '@libs/models/shifts';
import dayjs from 'dayjs';

type ShiftListRequestParams = {
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  externalLocationNames?: string[];
  qualificationIds?: string[];
  staffIds?: string[];
  statuses?: ShiftStatus[];
  specialityIds?: string[];
  facilityTenantIds?: string[];
  shiftTimeTypes?: ShiftType[];
  orderBy?: string[];
  series?: string[];
  rotation?: string[];
  search?: string;
};

type ShiftStatus =
  | 'Pending'
  | 'Assigned'
  | 'Reassigned'
  | 'NotAllowed'
  | 'Canceled'
  | 'InProgress'
  | 'Unfilled'
  | 'Completed';
type ShiftType = 'Eve' | 'Day' | 'Noc';

export async function fetchShiftsList(
  page = 1,
  pageSize = 10,
  filters?: ShiftListRequestParams,
): Promise<PaginatedResponse<Shift[]>> {
  const response = await request.get<PaginatedResponse<ShiftListResponse>>('/v2/shifts/list-view', {
    params: {
      pageNumber: page,
      pageSize,

      startDate: filters?.startDate ? dayjs(filters?.startDate).toServerDateTimeFormat() : null,
      endDate: filters?.endDate ? dayjs(filters?.endDate).toServerDateTimeFormat() : null,
      timeZone: filters?.timeZone,
      externalLocationNames_in: filters?.externalLocationNames,
      qualifications_in: filters?.qualificationIds,
      staff_in: filters?.staffIds,
      statuses_in: filters?.statuses,
      specialities_in: filters?.specialityIds,
      facilities_in: filters?.facilityTenantIds,
      shifttimetypes_in: filters?.shiftTimeTypes,
      orderBy_in: filters?.orderBy,
      series_in: filters?.series,
      rotation_in: filters?.rotation,
      search: filters?.search,
    },
  });
  return {
    ...response.data,
    items: response.data.items.map((data) => ({
      ...data,
      recurrenceId: data.shiftRecurrenceId || '',
      externalId: data.externalId || '',
      shiftStart: dayjs(data.startDate.dateTime),
      shiftLength: new Date(data.endDate.dateTime).getTime() - new Date(data.startDate.dateTime).getTime(),
      timeZone: data.startDate.timeZone,
      recurrence: data.recurrenceType
        ? (data.recurrenceType.toLowerCase() as 'single' | 'series' | 'rotation')
        : data.shiftRotationId
          ? 'rotation'
          : data.shiftSeriesId
            ? 'series'
            : 'single',
      qualifications: data.qualificationToPayFilters,
      specialty: data.specialityToPay?.title || undefined,
      notes: data.note ? [data.note] : [],
      externalAssignRequest: !!data.assignRequest,
      // externalAssignRequest: !!data.staff,
      assignRequest: data.assignRequest
        ? {
            ...data.assignRequest,
            staff: {
              ...data.assignRequest.staff,
              fullName: [data.assignRequest.staff.firstName, data.assignRequest.staff.lastName]
                .filter(Boolean)
                .join(' '),
            },
          }
        : null,
      // assignRequest: data.staff
      //   ? {
      //       declineReason: '',
      //       status: 'Pending',
      //       staff: {
      //         firstName: data.staff.firstName,
      //         lastName: data.staff.lastName,
      //         id: data.staff.id,
      //         imageUrl: data.staff.imageUrl || '',
      //         fullName: [data.staff.firstName, data.staff.lastName].filter(Boolean).join(' '),
      //       },
      //     }
      //   : null,
      assignee: data.staff && {
        ...data.staff,
        // TODO: get from server
        assignedByClient: false,
        avatar: data.staff.imageUrl,
        name: [data.staff.firstName, data.staff.lastName].filter(Boolean).join(' '),
      },
      location: {
        name: data.location.name,
        city: data.location.city,
        state: data.location.state,
        address1: data.location.firstAddress,
        address2: data.location.secondAddress,
      },
      allowedMultipleAssignees: data.canHasMultipleAssignee,
      isUrgent: !!data.isUrgent,
      isArchive: !!data.isArchive,
    })),
  };
}

type ShiftListResponse = {
  id: string;
  externalId: string | null;
  startDate: {
    dateTime: string;
    timeZone: string;
  };
  assignRequest?: {
    status: 'Pending' | 'Approved' | 'Declined';
    staff: {
      id: string; // 'staffmember-00000000000000000000000000';
      firstName: string;
      lastName: string;
      imageUrl: string;
    };
    declineReason: string;
  };
  endDate: {
    dateTime: string;
    timeZone: string;
  };
  qualificationToPayFilters: {
    id: string;
    name: string;
    abbreviation: string;
    category: string;
  }[];
  // @deprecated
  chosenQualifications:
    | {
        id: string;
        name: string;
        abbreviation: string;
        category: string;
      }[]
    | null;
  specialityToPay: {
    id: string;
    title: string;
    category: string;
  } | null;
  status: ShiftStatus;
  shiftTimeType: 'Eve' | 'Day' | 'Noc';
  note: string;
  tags: string[];
  staff: {
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  } | null;
  shiftSeriesId?: string | null;
  shiftRotationId?: string | null;
  shiftRecurrenceId: string | null;
  recurrenceType: 'Series' | 'Rotation';
  location: {
    city: string;
    state: string;
    name: string;
    firstAddress: string;
    secondAddress: string;
  };
  facilityTenantId: string;
  canHasMultipleAssignee: boolean;
  isUrgent: boolean;
  isArchive: boolean;
}[];
